/**
 * Template Builder Page (Mercell Admin Only)
 * 
 * Create and manage field templates for all register types.
 * Templates define which fields appear in a register and how they're organized into cards.
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templateApi, catalogApi } from '../services/api';
import { isPlatformAdmin } from '../utils/adminAccess';
import type { Template, TemplateCategory, TemplateField, CatalogField, EnabledModules } from '../types/models';
import { TemplatePreview } from '../components/TemplatePreview';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusTable } from '../components/NimbusTable';
import { PageHeader } from '../components/PageHeader';

type ViewMode = 'list' | 'create' | 'edit';

const CATEGORIES: Array<{ value: TemplateCategory; label: string }> = [
  { value: 'risk', label: 'Risk' },
  { value: 'incident', label: 'Incident' },
  { value: 'issue', label: 'Issue' },
  { value: 'remediation', label: 'Remediation' },
  { value: 'threat', label: 'Threat' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'control', label: 'Control' },
];

export const TemplateBuilderPage: React.FC = () => {
  const userId = localStorage.getItem('userId') || '';
  const userEmail = localStorage.getItem('userEmail') || '';
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory>('risk');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    templateName: string;
    displayName: string;
    description: string;
    isActive: boolean;
    category: TemplateCategory;
    cards: Record<string, string>;
    fields: TemplateField[];
    enabledModules: EnabledModules;
  }>({
    templateName: '',
    displayName: '',
    description: '',
    isActive: true,
    category: 'risk',
    cards: {},
    fields: [],
    enabledModules: {
      documentManager: true,
      raciManager: true,
      linkedArtefacts: true,
      requirementsTracker: true,
    },
  });

  // Card editor state
  const [newCardNumber, setNewCardNumber] = useState('');
  const [newCardName, setNewCardName] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');

  // Fetch templates
  const { data: templates = [], isLoading: loadingTemplates } = useQuery<Template[]>({
    queryKey: ['templates', selectedCategory],
    queryFn: () => templateApi.list(selectedCategory),
  });

  // Fetch catalog fields for selected category
  const { data: catalogFields = [], isLoading: loadingCatalog } = useQuery<CatalogField[]>({
    queryKey: ['catalog', formData.category],
    queryFn: () => catalogApi.listByCategory(formData.category),
    enabled: viewMode !== 'list',
  });

  // Filter catalog fields based on search
  const filteredCatalogFields = useMemo(() => {
    if (!catalogSearch.trim()) return catalogFields;
    
    const searchLower = catalogSearch.toLowerCase();
    return catalogFields.filter((field: CatalogField) => 
      field.fieldName?.toLowerCase().includes(searchLower) ||
      field.fieldId?.toLowerCase().includes(searchLower)
    );
  }, [catalogFields, catalogSearch]);

  // Group catalog fields by card
  const fieldsByCard = useMemo(() => {
    const grouped = new Map<number, CatalogField[]>();
    filteredCatalogFields.forEach((field: CatalogField) => {
      const cardNum = field.cardNumber || 0;
      if (!grouped.has(cardNum)) {
        grouped.set(cardNum, []);
      }
      grouped.get(cardNum)!.push(field);
    });
    // Sort fields within each card by displayOrder
    grouped.forEach((fields: CatalogField[]) => {
      fields.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
    });
    return grouped;
  }, [filteredCatalogFields]);

  // Get fields already assigned to template
  const assignedFieldIds = useMemo(() => {
    return new Set(formData.fields.map((f: TemplateField) => f.fieldId));
  }, [formData.fields]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: templateApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      resetForm();
      setViewMode('list');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: any }) =>
      templateApi.update(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      resetForm();
      setViewMode('list');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: templateApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  // Handlers
  const resetForm = () => {
    setFormData({
      templateName: '',
      displayName: '',
      description: '',
      isActive: true,
      category: selectedCategory,
      cards: {},
      fields: [],
      enabledModules: {
        documentManager: true,
        raciManager: true,
        linkedArtefacts: true,
        requirementsTracker: true,
      },
    });
    setEditingTemplate(null);
    setCatalogSearch('');
  };

  const handleCreateNew = () => {
    resetForm();
    setFormData(prev => ({ ...prev, category: selectedCategory }));
    setViewMode('create');
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setFormData({
      templateName: template.templateName,
      displayName: template.displayName,
      description: template.description,
      isActive: template.isActive,
      category: template.category,
      cards: template.cards,
      fields: template.fields,
      enabledModules: template.enabledModules || {
        documentManager: true,
        raciManager: true,
        linkedArtefacts: true,
        requirementsTracker: true,
      },
    });
    setViewMode('edit');
  };

  const handleClone = (template: Template) => {
    setFormData({
      templateName: `${template.templateName}_copy`,
      displayName: `${template.displayName} (Copy)`,
      description: template.description,
      isActive: false,
      category: template.category,
      cards: { ...template.cards },
      fields: [...template.fields],
      enabledModules: template.enabledModules || {
        documentManager: true,
        raciManager: true,
        linkedArtefacts: true,
        requirementsTracker: true,
      },
    });
    setViewMode('create');
  };

  const handleDelete = async (templateId: string) => {
    const ok = await swal.confirmDelete('this template');
    if (!ok) return;
    await deleteMutation.mutateAsync(templateId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.templateName || !formData.displayName || !formData.category) {
      toast.warning('Please fill in all required fields');
      return;
    }

    try {
      if (editingTemplate) {
        await updateMutation.mutateAsync({
          templateId: editingTemplate.templateId,
          data: {
            displayName: formData.displayName,
            description: formData.description,
            isActive: formData.isActive,
            cards: formData.cards,
            fields: formData.fields,
            enabledModules: formData.enabledModules,
          },
        });
      } else {
        await createMutation.mutateAsync({
          templateName: formData.templateName,
          category: formData.category,
          displayName: formData.displayName,
          description: formData.description,
          isActive: formData.isActive,
          cards: formData.cards,
          fields: formData.fields,
          enabledModules: formData.enabledModules,
        });
      }
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleAddCard = () => {
    if (!newCardNumber || !newCardName) {
      toast.warning('Please enter both card number and name');
      return;
    }

    if (formData.cards[newCardNumber]) {
      toast.error('Card number already exists');
      return;
    }

    setFormData(prev => ({
      ...prev,
      cards: { ...prev.cards, [newCardNumber]: newCardName },
    }));
    setNewCardNumber('');
    setNewCardName('');
  };

  const handleRemoveCard = (cardNumber: string) => {
    const { [cardNumber]: removed, ...remainingCards } = formData.cards;
    setFormData(prev => ({
      ...prev,
      cards: remainingCards,
      // Remove fields assigned to this card
      fields: prev.fields.filter((f: TemplateField) => f.cardNumber !== parseInt(cardNumber)),
    }));
  };

  const handleRenameCard = (cardNumber: string, newName: string) => {
    setFormData(prev => ({
      ...prev,
      cards: { ...prev.cards, [cardNumber]: newName },
    }));
  };

  const handleAddField = (field: CatalogField, cardNumber: number) => {
    if (assignedFieldIds.has(field.fieldId)) {
      toast.info('This field is already assigned to the template');
      return;
    }

    const maxOrder = formData.fields
      .filter((f: TemplateField) => f.cardNumber === cardNumber)
      .reduce((max: number, f: TemplateField) => Math.max(max, f.displayOrder), 0);

    setFormData(prev => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          fieldId: field.fieldId,
          cardNumber,
          displayOrder: maxOrder + 1,
        },
      ],
    }));
  };

  const handleRemoveField = (fieldId: string) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((f: TemplateField) => f.fieldId !== fieldId),
    }));
  };

  const handleReorderField = (fieldId: string, direction: 'up' | 'down') => {
    const fieldIndex = formData.fields.findIndex((f: TemplateField) => f.fieldId === fieldId);
    if (fieldIndex === -1) return;

    const field = formData.fields[fieldIndex];
    const sameCardFields = formData.fields
      .map((f: TemplateField, idx: number) => ({ ...f, originalIndex: idx }))
      .filter((f: TemplateField & { originalIndex: number }) => f.cardNumber === field.cardNumber)
      .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

    const cardFieldIndex = sameCardFields.findIndex((f: any) => f.fieldId === fieldId);
    const swapIndex = direction === 'up' ? cardFieldIndex - 1 : cardFieldIndex + 1;

    if (swapIndex < 0 || swapIndex >= sameCardFields.length) return;

    const newFields = [...formData.fields];
    const temp = newFields[sameCardFields[cardFieldIndex].originalIndex].displayOrder;
    newFields[sameCardFields[cardFieldIndex].originalIndex].displayOrder = 
      newFields[sameCardFields[swapIndex].originalIndex].displayOrder;
    newFields[sameCardFields[swapIndex].originalIndex].displayOrder = temp;

    setFormData(prev => ({ ...prev, fields: newFields }));
  };

  // Future enhancement: Move field between cards
  // const handleMoveFieldToCard = (fieldId: string, newCardNumber: number) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     fields: prev.fields.map((f: TemplateField) =>
  //       f.fieldId === fieldId ? { ...f, cardNumber: newCardNumber } : f
  //     ),
  //   }));
  // };

  // Get template fields with catalog data merged
  const enrichedTemplateFields = useMemo(() => {
    const catalogMap = new Map(catalogFields.map((f: CatalogField) => [f.fieldId, f]));
    return formData.fields
      .map((tf: TemplateField) => ({
        ...tf,
        catalog: catalogMap.get(tf.fieldId),
      }))
      .filter((f: any) => f.catalog); // Only show fields that exist in catalog
  }, [formData.fields, catalogFields]);

  // Group template fields by card
  const templateFieldsByCard = useMemo(() => {
    const grouped = new Map<number, typeof enrichedTemplateFields>();
    enrichedTemplateFields.forEach((field: any) => {
      if (!grouped.has(field.cardNumber)) {
        grouped.set(field.cardNumber, []);
      }
      grouped.get(field.cardNumber)!.push(field);
    });
    // Sort by displayOrder
    grouped.forEach((fields: any[]) => {
      fields.sort((a: any, b: any) => a.displayOrder - b.displayOrder);
    });
    return grouped;
  }, [enrichedTemplateFields]);

  if (!isPlatformAdmin(userEmail)) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          Unauthorized: This page is only accessible to Mercell administrators.
        </div>
      </div>
    );
  }

  if (loadingTemplates) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-layout-grid-line"
        title="Template Builder"
        subtitle="Create and manage field templates for registers"
        actions={
          <>
            {viewMode === 'list' && (
              <button className="np-btn np-btn-primary" onClick={handleCreateNew}>
                <i className="ri-add-line"></i>
                Create Template
              </button>
            )}
            {viewMode !== 'list' && (
              <button className="np-btn np-btn-ghost" onClick={() => { resetForm(); setViewMode('list'); }}>
                <i className="ri-arrow-left-line"></i>
                Back to List
              </button>
            )}
          </>
        }
      />

      {viewMode === 'list' && (
        <div className="np-card">
          <div className="np-card-head">
            <h2>Templates</h2>
            <select
              className="np-select"
              style={{ maxWidth: '200px', marginLeft: 'auto' }}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as TemplateCategory)}
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
            {templates.length === 0 ? (
              <div className="np-card-body">
                <div className="text-center py-5">
                  <i className="ri-file-list-3-line" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                  <p className="np-sub mt-3">
                    No templates found for {CATEGORIES.find(c => c.value === selectedCategory)?.label}.
                  </p>
                </div>
              </div>
            ) : (
              <NimbusTable thead={<tr>
                  <th>Name</th>
                  <th>Display Name</th>
                  <th>Fields</th>
                  <th>Cards</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>}>
                    {templates.map((template: Template) => (
                      <tr key={template.templateId}>
                        <td>
                          <code>{template.templateName}</code>
                        </td>
                        <td>
                          <strong>{template.displayName}</strong>
                          {template.description && (
                            <div className="small np-sub">{template.description}</div>
                          )}
                        </td>
                        <td>
                          <span className="np-chip np-tone-primary">{template.fields.length} fields</span>
                        </td>
                        <td>
                          <span className="np-chip np-tone-info">{Object.keys(template.cards).length} cards</span>
                        </td>
                        <td>
                          {template.isActive ? (
                            <span className="np-chip np-tone-success">Active</span>
                          ) : (
                            <span className="np-chip np-tone-neutral">Inactive</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="np-icon-btn"
                              onClick={() => handleEdit(template)}
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              className="np-icon-btn"
                              onClick={() => handleClone(template)}
                              title="Clone"
                            >
                              <i className="ri-file-copy-line"></i>
                            </button>
                            <button
                              className="np-icon-btn np-icon-btn-danger"
                              onClick={() => handleDelete(template.templateId)}
                              title="Delete"
                            >
                              <i className="ri-delete-bin-7-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
              </NimbusTable>
            )}
        </div>
      )}

      {(viewMode === 'create' || viewMode === 'edit') && (
        <form onSubmit={handleSubmit}>
          {/* Template Information Row */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="np-card">
                <div className="np-card-head">
                  <h2>Template Information</h2>
                </div>
                <div className="np-card-body">
                  <div className="np-field">
                    <label>
                      Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="np-select"
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as TemplateCategory }))}
                      disabled={viewMode === 'edit'}
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="np-field">
                    <label>
                      Template Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="np-input"
                      value={formData.templateName}
                      onChange={(e) => setFormData(prev => ({ ...prev, templateName: e.target.value }))}
                      disabled={viewMode === 'edit'}
                      placeholder="e.g., standard, comprehensive"
                    />
                    <p className="np-hint">
                      Lowercase identifier used in API calls
                    </p>
                  </div>

                  <div className="np-field">
                    <label>
                      Display Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="np-input"
                      value={formData.displayName}
                      onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                      placeholder="e.g., Standard Risk Template"
                    />
                  </div>

                  <div className="np-field">
                    <label>Description</label>
                    <textarea
                      className="np-textarea"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what this template includes..."
                    />
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <label className="np-switch mb-0">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      />
                      <span className="np-switch-track"></span>
                      <span className="np-switch-thumb"></span>
                    </label>
                    <label className="mb-0" htmlFor="isActive">
                      Active (visible in dropdowns)
                    </label>
                  </div>

                  <button type="button" className="np-btn np-btn-ghost w-100 mb-2" onClick={() => setShowPreview(true)}>
                    <i className="ri-eye-line"></i>
                    Preview Template
                  </button>

                  <button type="submit" className="np-btn np-btn-primary w-100" disabled={createMutation.isPending || updateMutation.isPending}>
                    {(createMutation.isPending || updateMutation.isPending) ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line"></i>
                        {viewMode === 'edit' ? 'Update Template' : 'Create Template'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Module Configuration Row */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="np-card">
                <div className="np-card-head">
                  <h2>Available Modules</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-3">Configure which features are available on registers using this template</p>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-start gap-2">
                        <label className="np-switch mb-0" style={{ marginTop: '0.15rem' }}>
                          <input
                            type="checkbox"
                            id="moduleDocumentManager"
                            checked={formData.enabledModules.documentManager}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              enabledModules: { ...prev.enabledModules, documentManager: e.target.checked }
                            }))}
                          />
                          <span className="np-switch-track"></span>
                          <span className="np-switch-thumb"></span>
                        </label>
                        <label className="mb-0" htmlFor="moduleDocumentManager">
                          <i className="ri-file-text-line me-1"></i>
                          <strong>Document Manager</strong>
                          <div className="small np-sub">Upload and manage file attachments</div>
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-start gap-2">
                        <label className="np-switch mb-0" style={{ marginTop: '0.15rem' }}>
                          <input
                            type="checkbox"
                            id="moduleRaciManager"
                            checked={formData.enabledModules.raciManager}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              enabledModules: { ...prev.enabledModules, raciManager: e.target.checked }
                            }))}
                          />
                          <span className="np-switch-track"></span>
                          <span className="np-switch-thumb"></span>
                        </label>
                        <label className="mb-0" htmlFor="moduleRaciManager">
                          <i className="ri-team-line me-1"></i>
                          <strong>RACI Manager</strong>
                          <div className="small np-sub">Assign Responsible, Accountable, Consulted, Informed roles</div>
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-start gap-2">
                        <label className="np-switch mb-0" style={{ marginTop: '0.15rem' }}>
                          <input
                            type="checkbox"
                            id="moduleLinkedArtefacts"
                            checked={formData.enabledModules.linkedArtefacts}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              enabledModules: { ...prev.enabledModules, linkedArtefacts: e.target.checked }
                            }))}
                          />
                          <span className="np-switch-track"></span>
                          <span className="np-switch-thumb"></span>
                        </label>
                        <label className="mb-0" htmlFor="moduleLinkedArtefacts">
                          <i className="ri-links-line me-1"></i>
                          <strong>Linked Artefacts</strong>
                          <div className="small np-sub">Link to related controls, risks, and other entities</div>
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-start gap-2">
                        <label className="np-switch mb-0" style={{ marginTop: '0.15rem' }}>
                          <input
                            type="checkbox"
                            id="moduleRequirementsTracker"
                            checked={formData.enabledModules.requirementsTracker}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              enabledModules: { ...prev.enabledModules, requirementsTracker: e.target.checked }
                            }))}
                          />
                          <span className="np-switch-track"></span>
                          <span className="np-switch-thumb"></span>
                        </label>
                        <label className="mb-0" htmlFor="moduleRequirementsTracker">
                          <i className="ri-task-line me-1"></i>
                          <strong>Requirements Tracker</strong>
                          <div className="small np-sub">Track functional and non-functional requirements</div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cards Row */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="np-card">
                <div className="np-card-head">
                  <h2>Cards & Assigned Fields</h2>
                  <span className="np-chip np-tone-neutral np-tabular">{enrichedTemplateFields.length} fields assigned</span>
                </div>
                <div className="np-card-body">
                  <div className="np-field">
                    <label>Add New Card</label>
                    <div className="input-group mb-2">
                      <input
                        type="number"
                        className="np-input"
                        placeholder="Card #"
                        value={newCardNumber}
                        onChange={(e) => setNewCardNumber(e.target.value)}
                        style={{ maxWidth: '80px' }}
                      />
                      <input
                        type="text"
                        className="np-input"
                        placeholder="Card Name"
                        value={newCardName}
                        onChange={(e) => setNewCardName(e.target.value)}
                      />
                      <button type="button" className="np-btn np-btn-primary" onClick={handleAddCard}>
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>

                  {Object.keys(formData.cards).length === 0 ? (
                    <div className="text-center py-4">
                      <i className="ri-layout-grid-line np-sub" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                      <p className="np-sub mt-2 mb-0">No cards defined</p>
                    </div>
                  ) : (
                    <div className="accordion" id="cardsAccordion">
                      {Object.entries(formData.cards)
                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                        .map(([cardNum, cardName]: [string, string]) => {
                          const fieldsInCard = templateFieldsByCard.get(parseInt(cardNum)) || [];
                          return (
                            <div
                              key={cardNum}
                              className="accordion-item mb-2"
                              style={{ border: '1px solid var(--np-border)', borderRadius: 'var(--np-radius)', overflow: 'hidden' }}
                            >
                              <h2 className="accordion-header">
                                <div className="d-flex align-items-center p-2" style={{ background: 'var(--np-surface-2)' }}>
                                  <button
                                    className="accordion-button collapsed py-2"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#cardContent${cardNum}`}
                                    style={{ flexGrow: 0, width: 'auto', marginRight: '0.5rem' }}
                                  >
                                  </button>
                                  <span className="np-chip np-tone-primary me-2">{cardNum}</span>
                                  <input
                                    type="text"
                                    className="np-input me-2"
                                    style={{ padding: '0.3rem 0.6rem' }}
                                    value={cardName}
                                    onChange={(e) => handleRenameCard(cardNum, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className="np-chip np-tone-neutral me-2">{fieldsInCard.length} fields</span>
                                  <button
                                    type="button"
                                    className="np-icon-btn np-icon-btn-danger"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveCard(cardNum); }}
                                    title="Delete card"
                                  >
                                    <i className="ri-delete-bin-7-line"></i>
                                  </button>
                                </div>
                              </h2>
                              <div id={`cardContent${cardNum}`} className="accordion-collapse collapse">
                                <div className="accordion-body">
                                  {fieldsInCard.length === 0 ? (
                                    <div className="text-center py-3">
                                      <small className="np-sub"><i className="ri-file-list-3-line me-1"></i>No fields assigned - use "Add to Card" below</small>
                                    </div>
                                  ) : (
                                    <div className="np-list">
                                      {fieldsInCard.map((field: any, index: number) => (
                                        <div key={field.fieldId} className="np-list-item">
                                          <div className="d-flex align-items-center flex-grow-1 gap-2">
                                            <span className="np-sub np-tabular" style={{ fontSize: '0.75rem' }}>#{index + 1}</span>
                                            <div>
                                              <div className="fw-medium">{field.catalog?.fieldName}</div>
                                              <div className="small np-sub">
                                                <code>{field.fieldId}</code>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="d-flex gap-1">
                                            <button
                                              type="button"
                                              className="np-icon-btn"
                                              onClick={() => handleReorderField(field.fieldId, 'up')}
                                              disabled={index === 0}
                                              title="Move up"
                                            >
                                              <i className="ri-arrow-up-line"></i>
                                            </button>
                                            <button
                                              type="button"
                                              className="np-icon-btn"
                                              onClick={() => handleReorderField(field.fieldId, 'down')}
                                              disabled={index === fieldsInCard.length - 1}
                                              title="Move down"
                                            >
                                              <i className="ri-arrow-down-line"></i>
                                            </button>
                                            <button
                                              type="button"
                                              className="np-icon-btn np-icon-btn-danger"
                                              onClick={() => handleRemoveField(field.fieldId)}
                                              title="Remove field"
                                            >
                                              <i className="ri-delete-bin-7-line"></i>
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Field Catalog Browser */}
          {Object.keys(formData.cards).length > 0 && (
            <div className="np-card">
              <div className="np-card-head">
                <h2>Field Catalog - Add Fields to Your Template</h2>
              </div>
              <div className="np-card-body">
                <p className="np-sub mb-3">
                  Click the <i className="ri-add-line"></i> button next to a field, then choose which card to add it to
                </p>
                {/* Search Box */}
                <div className="mb-3">
                  <div className="np-search" style={{ maxWidth: '100%' }}>
                    <i className="ri-search-line"></i>
                    <input
                      type="text"
                      placeholder="Search fields by name or ID..."
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                    />
                    {catalogSearch && (
                      <button
                        type="button"
                        className="np-icon-btn"
                        onClick={() => setCatalogSearch('')}
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>
                  {catalogSearch && (
                    <small className="np-sub mt-1 d-block">
                      Found {filteredCatalogFields.length} of {catalogFields.length} fields
                    </small>
                  )}
                </div>

                {loadingCatalog ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                    <p className="np-sub mt-2">Loading catalog...</p>
                  </div>
                ) : catalogFields.length === 0 ? (
                  <div className="np-banner np-tone-warning">
                    <i className="ri-error-warning-line"></i>
                    No fields found in catalog for category: {formData.category}
                  </div>
                ) : filteredCatalogFields.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="ri-search-line" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                    <p className="np-sub mt-2 mb-0">No fields match "{catalogSearch}"</p>
                    <button
                      type="button"
                      className="np-btn np-btn-ghost np-btn-sm"
                      onClick={() => setCatalogSearch('')}
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="row">
                    {Array.from(fieldsByCard.entries())
                      .sort(([a], [b]) => a - b)
                      .map(([cardNum, fields]: [number, CatalogField[]]) => (
                        <div key={cardNum} className="col-md-6 col-lg-4 mb-3">
                          <div className="np-card" style={{ background: 'var(--np-surface-2)' }}>
                            <div className="np-card-head" style={{ padding: '0.625rem 0.75rem 0' }}>
                              <small className="fw-bold">
                                Available Fields - Catalog Card {cardNum}: {fields[0]?.cardName || 'Unknown'}
                              </small>
                            </div>
                            <div className="np-card-body" style={{ padding: '0.5rem 0.75rem 0.75rem' }}>
                              <div className="np-list">
                                {fields.map((field: CatalogField) => {
                                  const isAssigned = assignedFieldIds.has(field.fieldId);
                                  return (
                                    <div
                                      key={field.fieldId}
                                      className="np-list-item"
                                      style={isAssigned ? { background: 'var(--bs-success-bg-subtle)' } : undefined}
                                    >
                                      <div className="flex-grow-1">
                                        <div className="small fw-medium">{field.fieldName}</div>
                                        <div className="np-sub" style={{ fontSize: '0.75rem' }}>
                                          <code>{field.fieldId}</code>
                                        </div>
                                      </div>
                                      {isAssigned ? (
                                        <span className="np-chip np-tone-success">
                                          <i className="ri-checkbox-circle-line"></i>
                                          Added
                                        </span>
                                      ) : (
                                        <div className="dropdown">
                                          <button
                                            type="button"
                                            className="np-btn np-btn-primary np-btn-sm dropdown-toggle"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                            title="Add to card"
                                          >
                                            <i className="ri-add-line"></i>
                                            Add to Card
                                          </button>
                                          <ul className="dropdown-menu dropdown-menu-end">
                                            {Object.entries(formData.cards)
                                              .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                              .map(([cardNum, cardName]) => (
                                                <li key={cardNum}>
                                                  <button
                                                    type="button"
                                                    className="dropdown-item"
                                                    onClick={() => handleAddField(field, parseInt(cardNum))}
                                                  >
                                                    <span className="np-chip np-tone-primary me-2">{cardNum}</span>
                                                    {cardName}
                                                  </button>
                                                </li>
                                              ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      )}

      {/* Template Preview Modal */}
      {showPreview && (
        <TemplatePreview
          template={{
            ...formData,
            templateId: editingTemplate?.templateId || 'preview',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: userId,
            updatedBy: userId,
          }}
          catalogFields={catalogFields}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};
